import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SupportButton from '../components/SupportButton';
import TransactionList from '../components/transaction/TransactionList';
import LoginDialog from '../components/auth/LoginDialog';
import RegisterDialog from '../components/auth/RegisterDialog';
import { Transaction } from '../types/api';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TransactionTracking: React.FC = () => {
  const { t } = useTranslation('tx');
  // Auth source-of-truth = AuthContext.user. Previous implementation
  // peeked at localStorage.auth_token directly, which (a) showed
  // stale data when one account logged out + another logged in on
  // the same SPA navigation (the page never refetched), and (b)
  // drifted from the header avatar that does read AuthContext.
  // authLoading covers the Firebase bootstrap window (and any pending
  // Mosan auto-login redirect) so we don't flash 'No Transaction /
  // Please login' to a user who's about to land authenticated.
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const [transactions, setTransactions] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  const gmtOffset = (() => {
    const offset = -new Date().getTimezoneOffset();
    const h = Math.floor(Math.abs(offset) / 60);
    const m = Math.abs(offset) % 60;
    const sign = offset >= 0 ? '+' : '-';
    return m > 0 ? `GMT${sign}${h}:${String(m).padStart(2, '0')}` : `GMT${sign}${h}`;
  })();

  // Re-fetch whenever the auth subject changes (login, logout, account
  // switch). Clear state on logout / account switch so a freshly-landed
  // user never sees the previous user's rows flash before the response.
  useEffect(() => {
    // Wait for auth bootstrap to settle before deciding anything —
    // otherwise unauthenticated branch runs prematurely and clears
    // state right before the real user lands.
    if (authLoading) return;
    if (!isAuthenticated) {
      setTransactions([]);
      setHasSearched(false);
      return;
    }
    setTransactions([]);
    setHasSearched(false);
    fetchTransactions();
    // user.id (backend UUID / firebase uid fallback) is the swap
    // signal — stable across token refreshes and populated for every
    // auth path including Mosan WebView auto-login where email is
    // synthetic / empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.id]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTransactions();

      const transformedTransactions = response.data.map((tx: Transaction) => {
        // Pass raw status keys so TransactionCard can drive both label
        // (via t()) and color (via the same key) from a single source —
        // translating here would break the card's color-by-string switch
        // when locale flips.
        // Status resolution: payment_status is the source-of-truth until
        // the buyer actually pays. Only after payment_status='paid' do we
        // fall through to the fulfillment status (success/failed/processing).
        // Otherwise the UI flashed "Processing" on already-expired orders
        // because the row's `status` column keeps reading 'pending' until
        // the payment lifecycle terminates.
        const statusKey =
          tx.status === 'refunded' ? 'refunded' :
          tx.payment_status === 'paid'
            ? (tx.status === 'success' ? 'success'
                : tx.status === 'failed' ? 'failed'
                : 'processing')
            : tx.payment_status === 'pending' ? 'awaiting_payment'
            : tx.payment_status === 'expired' ? 'expired'
            : tx.payment_status === 'failed' ? 'failed'
            : 'processing';
        return {
          id: tx.reference,
          type: t('type_topup'),
          date: new Date(tx.created_at).toLocaleString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }) + ` (${gmtOffset})`,
          status: statusKey,
          game: {
            name: tx.product_name,
            image: tx.product_image || '',
          },
          product: tx.product_item_name,
          total: tx.total_amount,
        };
      });

      setTransactions(transformedTransactions);
      setHasSearched(true);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setTransactions([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
          {t('title')}
        </h1>

        {authLoading ? (
          // Firebase / Mosan auto-login still resolving — show a
          // neutral spinner instead of the guest-empty state so we
          // don't flash 'Please login' to a user about to land
          // authenticated.
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : !isAuthenticated ? (
          <TransactionList
            transactions={[]}
            hasSearched={true}
            emptyVariant="guest"
            onLogin={() => setLoginOpen(true)}
          />
        ) : loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : (
          <TransactionList
            transactions={transactions}
            hasSearched={hasSearched}
          />
        )}
      </main>

      <Footer />
      <SupportButton />

      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSwitchToRegister={() => {
          setLoginOpen(false);
          setRegisterOpen(true);
        }}
      />
      <RegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSwitchToLogin={() => {
          setRegisterOpen(false);
          setLoginOpen(true);
        }}
      />
    </div>
  );
};

export default TransactionTracking;
